import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Upload, FileText, AlertTriangle, Trash2,
  Loader2, History, ShieldAlert, Wrench,
  ChevronDown, ChevronRight, X, Store, AlertOctagon, Gauge, FileDown
} from 'lucide-react';
import { TabLoader } from './TabLoader';

const API = process.env.REACT_APP_BACKEND_URL;

const PAYER_COLORS = {
  customer: 'bg-blue-50 text-blue-700 border-blue-200',
  insurance: 'bg-red-50 text-red-700 border-red-200',
  warranty: 'bg-purple-50 text-purple-700 border-purple-200',
  free: 'bg-green-50 text-green-700 border-green-200',
  unknown: 'bg-gray-50 text-gray-600 border-gray-200',
};

const SERVICE_TYPE_LABELS = {
  free_service: 'Free Service',
  paid_service: 'Paid Service',
  running_repair: 'Running Repair',
  accidental_repair: 'Accidental Repair',
  campaign: 'Campaign',
  other: 'Other',
};

const SERVICE_TYPE_COLORS = {
  free_service: 'bg-emerald-50 text-emerald-700',
  paid_service: 'bg-sky-50 text-sky-700',
  running_repair: 'bg-amber-50 text-amber-700',
  accidental_repair: 'bg-red-50 text-red-700',
  campaign: 'bg-violet-50 text-violet-700',
  other: 'bg-gray-50 text-gray-600',
};

const UPLOAD_STEPS = [
  'Uploading document...',
  'AI is reading the document...',
  'Extracting service records...',
  'Analyzing for suspicious patterns...',
  'Finalizing results...',
];

export default function ServiceHistoryTab({ inspection, currentOdometer, onUpdateEditData }) {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState(0);
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [allPublic, setAllPublic] = useState(true);
  const [pdfInclusion, setPdfInclusion] = useState('none');
  const [pendingUpload, setPendingUpload] = useState(null); // holds extracted data awaiting confirmation
  const fileInputRef = useRef(null);
  const stepIntervalRef = useRef(null);

  // Odometer tamper detection
  const lastServiceKm = records.length > 0
    ? Math.max(...records.map(r => r.km_reading || 0))
    : null;
  const odometerTampered = lastServiceKm && currentOdometer && currentOdometer > 0
    && currentOdometer < lastServiceKm;
  const kmDifference = odometerTampered ? lastServiceKm - currentOdometer : 0;

  // Detect duplicates: same date + km_reading + service_type appearing more than once
  const hasDuplicates = (() => {
    const seen = new Set();
    for (const r of records) {
      const key = `${r.date}|${r.km_reading}|${r.service_type}`;
      if (seen.has(key)) return true;
      seen.add(key);
    }
    return false;
  })();

  const handleDeduplicate = async () => {
    try {
      const res = await axios.post(
        `${API}/api/inspections/${inspectionId}/service-history/deduplicate`,
        {},
        { headers }
      );
      toast.success(`Removed ${res.data.duplicates_removed} duplicates (${res.data.records_remaining} remaining)`);
      fetchRecords();
    } catch {
      toast.error('Failed to remove duplicates');
    }
  };

  const inspectionId = inspection?.id;
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchRecords = useCallback(async () => {
    if (!inspectionId) return;
    setLoading(true);
    try {
      const [histRes, pdfRes] = await Promise.all([
        axios.get(`${API}/api/inspections/${inspectionId}/service-history`, { headers }),
        axios.get(`${API}/api/inspections/${inspectionId}/service-history/pdf-inclusion`, { headers }).catch(() => ({ data: { include_in_pdf: 'none' } })),
      ]);
      setRecords(histRes.data.records || []);
      setSummary(histRes.data.summary || null);
      const pub = (histRes.data.records || []).every(r => r.is_public);
      setAllPublic(pub);
      setPdfInclusion(pdfRes.data.include_in_pdf || 'none');
    } catch (err) {
      console.error('Failed to fetch service history:', err);
    } finally {
      setLoading(false);
    }
  }, [inspectionId]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    return () => { if (stepIntervalRef.current) clearInterval(stepIntervalRef.current); };
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadStep(0);

    let step = 0;
    stepIntervalRef.current = setInterval(() => {
      step = Math.min(step + 1, UPLOAD_STEPS.length - 1);
      setUploadStep(step);
    }, 5000);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(
        `${API}/api/inspections/${inspectionId}/service-history/upload`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );
      const data = res.data;

      // Check for vehicle mismatch
      if (data.vehicle_comparison?.has_mismatch) {
        // Show confirmation dialog — don't save yet
        setPendingUpload(data);
        toast.warning('Vehicle mismatch detected — please review before saving', { duration: 5000 });
      } else {
        // No mismatch — save directly
        await confirmSave(data);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
      setUploadStep(0);
      if (stepIntervalRef.current) clearInterval(stepIntervalRef.current);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const confirmSave = async (data) => {
    // Prevent double-calls (concurrent uploads)
    if (confirmSave._running) return;
    confirmSave._running = true;
    try {
      await axios.post(
        `${API}/api/inspections/${inspectionId}/service-history/confirm-upload`,
        { records: data.records },
        { headers }
      );
      toast.success(`Saved ${data.records_extracted} service records`);

      if (data.summary?.suspicious_flags?.length > 0) {
        data.summary.suspicious_flags.forEach(flag => {
          toast.warning(flag, { duration: 8000 });
        });
      }
      if (data.auto_flagged && onUpdateEditData) {
        if (data.auto_flagged.accident_history) {
          onUpdateEditData('accident_history', true);
          toast.warning('Accidental repairs detected — "Accident History" auto-updated to YES', { duration: 6000 });
        }
        if (data.auto_flagged.flood_damage) {
          onUpdateEditData('flood_damage', true);
          toast.warning('Flood damage detected — "Flood Damage" auto-updated to YES', { duration: 6000 });
        }
      }
      setPendingUpload(null);
      fetchRecords();
    } catch (err) {
      toast.error('Failed to save service history');
    } finally {
      confirmSave._running = false;
    }
  };

  const cancelPendingUpload = () => {
    setPendingUpload(null);
    toast.info('Upload cancelled — existing records unchanged');
  };

  const toggleVisibility = async (recordId, isPublic) => {
    try {
      await axios.patch(
        `${API}/api/inspections/${inspectionId}/service-history/${recordId}/visibility`,
        { is_public: isPublic },
        { headers }
      );
      setRecords(prev => prev.map(r =>
        r.id === recordId ? { ...r, is_public: isPublic } : r
      ));
    } catch {
      toast.error('Failed to update visibility');
    }
  };

  const toggleAllVisibility = async (isPublic) => {
    try {
      await axios.patch(
        `${API}/api/inspections/${inspectionId}/service-history/bulk-visibility`,
        { is_public: isPublic },
        { headers }
      );
      setRecords(prev => prev.map(r => ({ ...r, is_public: isPublic })));
      setAllPublic(isPublic);
      toast.success(isPublic ? 'All records visible on report' : 'All records hidden from report');
    } catch {
      toast.error('Failed to update visibility');
    }
  };

  const deleteRecord = async (recordId) => {
    try {
      await axios.delete(
        `${API}/api/inspections/${inspectionId}/service-history/${recordId}`,
        { headers }
      );
      setRecords(prev => prev.filter(r => r.id !== recordId));
      toast.success('Record deleted');
    } catch {
      toast.error('Failed to delete record');
    }
  };

  const deleteAll = async () => {
    if (!window.confirm('Delete all service history records? This cannot be undone.')) return;
    try {
      await axios.delete(
        `${API}/api/inspections/${inspectionId}/service-history`,
        { headers }
      );
      setRecords([]);
      setSummary(null);
      toast.success('All records deleted');
    } catch {
      toast.error('Failed to delete records');
    }
  };

  const handlePdfInclusionChange = async (value) => {
    try {
      await axios.patch(
        `${API}/api/inspections/${inspectionId}/service-history/pdf-inclusion`,
        { include_in_pdf: value },
        { headers }
      );
      setPdfInclusion(value);
      const labels = { wisedrive: 'WiseDrive B2C', sheerdrive: 'SheerDrive', both: 'Both reports', none: 'None' };
      toast.success(`Service history will be included in: ${labels[value]}`);
    } catch {
      toast.error('Failed to update PDF setting');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  const formatAmount = (amount) => {
    if (amount == null) return '-';
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  const formatKm = (km) => {
    if (km == null) return '-';
    return `${Number(km).toLocaleString('en-IN')} km`;
  };

  return (
    <TabsContent value="service-history" className="mt-0 focus-visible:ring-0 focus-visible:ring-offset-0">
      {!inspectionId ? <TabLoader message="Loading service history..." /> : (<>
      <div className="space-y-4" data-testid="service-history-tab">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">Service History</h3>
            {records.length > 0 && (
              <Badge variant="secondary" className="ml-1">{records.length} records</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {records.length > 0 && (
              <>
                <div className="flex items-center gap-2 mr-2 text-xs text-gray-500">
                  <span>Show on report</span>
                  <Switch
                    checked={allPublic}
                    onCheckedChange={toggleAllVisibility}
                    data-testid="toggle-all-visibility"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={deleteAll} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-all-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear All
                </Button>
                {hasDuplicates && (
                  <Button variant="outline" size="sm" onClick={handleDeduplicate} className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" data-testid="dedup-btn">
                    <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Remove Duplicates
                  </Button>
                )}
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls"
              onChange={handleUpload}
              className="hidden"
              data-testid="service-history-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              size="sm"
              data-testid="upload-service-history-btn"
            >
              {uploading ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Extracting...</>
              ) : (
                <><Upload className="h-4 w-4 mr-1" /> Upload PDF/Image</>
              )}
            </Button>
            {records.length > 0 && (
              <DownloadServiceHistoryPdf inspectionId={inspectionId} />
            )}
          </div>
        </div>

        {/* PDF Inclusion Selector */}
        {records.length > 0 && (
          <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border" data-testid="pdf-inclusion-selector">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <FileDown className="h-3.5 w-3.5" />
              <span className="font-medium">Include in PDF Report:</span>
            </div>
            <div className="flex items-center gap-1">
              {[
                { value: 'none', label: 'None' },
                { value: 'wisedrive', label: 'WiseDrive B2C' },
                { value: 'sheerdrive', label: 'SheerDrive' },
                { value: 'both', label: 'Both' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handlePdfInclusionChange(opt.value)}
                  className={`px-2.5 py-1 text-[11px] rounded-md font-medium transition-colors ${
                    pdfInclusion === opt.value
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border'
                  }`}
                  data-testid={`pdf-include-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="upload-progress">
            <div className="flex items-center gap-3 mb-3">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <span className="text-sm font-medium text-blue-800">{UPLOAD_STEPS[uploadStep]}</span>
            </div>
            <div className="w-full bg-blue-100 rounded-full h-1.5">
              <div
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-1000"
                style={{ width: `${((uploadStep + 1) / UPLOAD_STEPS.length) * 100}%` }}
              />
            </div>
            <p className="text-[10px] text-blue-500 mt-2">This typically takes 15-30 seconds depending on document size</p>
          </div>
        )}

        {/* Vehicle Mismatch Confirmation Dialog */}
        {pendingUpload && pendingUpload.vehicle_comparison?.has_mismatch && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4" data-testid="vehicle-mismatch-dialog">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-bold text-amber-800">Vehicle Mismatch Warning</h4>
                <p className="text-xs text-amber-700 mt-1">
                  The uploaded service history appears to belong to a different vehicle than the one being inspected.
                </p>

                <div className="mt-3 bg-white rounded-lg border border-amber-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-amber-100/50">
                        <th className="text-left px-3 py-1.5 font-medium text-amber-800">Field</th>
                        <th className="text-left px-3 py-1.5 font-medium text-gray-700">Inspection Vehicle</th>
                        <th className="text-left px-3 py-1.5 font-medium text-gray-700">Service History</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingUpload.vehicle_comparison.mismatches.map((m, i) => (
                        <tr key={i} className="border-t border-amber-100">
                          <td className="px-3 py-1.5 font-medium text-gray-700">{m.field}</td>
                          <td className="px-3 py-1.5 text-gray-900">{m.inspection_value || '-'}</td>
                          <td className="px-3 py-1.5 text-red-600 font-medium">{m.service_history_value || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-amber-700 mt-2">
                  {pendingUpload.records_extracted} records were extracted. Do you want to save them for this inspection anyway?
                </p>

                <div className="flex items-center gap-2 mt-3">
                  <Button
                    size="sm"
                    onClick={() => confirmSave(pendingUpload)}
                    data-testid="mismatch-proceed-btn"
                  >
                    Yes, Save Anyway
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelPendingUpload}
                    data-testid="mismatch-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Suspicious Flags Alert */}
        {summary?.suspicious_flags?.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="suspicious-flags-alert">
            <div className="flex items-center gap-2 mb-1.5">
              <ShieldAlert className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-800">Suspicious Patterns Detected</span>
            </div>
            <ul className="space-y-0.5">
              {summary.suspicious_flags.map((flag, i) => (
                <li key={i} className="text-xs text-red-700 flex items-center gap-1.5">
                  <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                  {flag}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Odometer Tamper Alert */}
        {odometerTampered && (
          <div className="bg-red-100 border-2 border-red-400 rounded-lg p-4" data-testid="odometer-tamper-alert">
            <div className="flex items-start gap-3">
              <AlertOctagon className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-red-800">Odometer Tampering Detected</h4>
                <p className="text-xs text-red-700 mt-1">
                  The current odometer reading (<strong>{Number(currentOdometer).toLocaleString('en-IN')} km</strong>) is
                  <strong> lower</strong> than the last service history KM reading (<strong>{Number(lastServiceKm).toLocaleString('en-IN')} km</strong>).
                </p>
                <p className="text-xs text-red-700 mt-0.5">
                  This indicates a <strong>KM reversal of {Number(kmDifference).toLocaleString('en-IN')} km</strong> — the odometer may have been tampered with.
                </p>
                <div className="flex items-center gap-4 mt-2 pt-2 border-t border-red-300">
                  <div className="flex items-center gap-1.5 text-xs">
                    <Gauge className="h-3.5 w-3.5 text-red-500" />
                    <span className="text-red-700">Current Odometer: <strong>{Number(currentOdometer).toLocaleString('en-IN')} km</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <History className="h-3.5 w-3.5 text-red-500" />
                    <span className="text-red-700">Last Service Record: <strong>{Number(lastServiceKm).toLocaleString('en-IN')} km</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {records.length > 0 && (
          <div className="grid grid-cols-4 gap-3" data-testid="summary-cards">
            <div className="bg-white border rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900">{summary?.total_services || 0}</div>
              <div className="text-xs text-gray-500">Total Services</div>
            </div>
            <div className={`border rounded-lg p-3 text-center ${summary?.accident_count > 0 ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
              <div className={`text-2xl font-bold ${summary?.accident_count > 0 ? 'text-red-600' : 'text-gray-900'}`}>{summary?.accident_count || 0}</div>
              <div className="text-xs text-gray-500">Accidents</div>
            </div>
            <div className={`border rounded-lg p-3 text-center ${summary?.insurance_claims > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}>
              <div className={`text-2xl font-bold ${summary?.insurance_claims > 0 ? 'text-amber-600' : 'text-gray-900'}`}>{summary?.insurance_claims || 0}</div>
              <div className="text-xs text-gray-500">Insurance Claims</div>
            </div>
            <div className="bg-white border rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900">
                {records[records.length - 1]?.km_reading ? formatKm(records[records.length - 1].km_reading) : '-'}
              </div>
              <div className="text-xs text-gray-500">Last KM Reading</div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !uploading && records.length === 0 && (
          <div className="border-2 border-dashed rounded-lg p-10 text-center" data-testid="empty-state">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h4 className="text-sm font-medium text-gray-700 mb-1">No Service History</h4>
            <p className="text-xs text-gray-500 mb-4 max-w-sm mx-auto">
              Upload the dealer service history PDF or image to auto-extract service records,
              detect accident history, and flag suspicious patterns.
            </p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-empty-state-btn"
            >
              <Upload className="h-4 w-4 mr-1" /> Upload Service History
            </Button>
            <p className="text-[10px] text-gray-400 mt-2">Supports: PDF, JPEG, PNG, XLSX</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}

        {/* Records Table - Simplified columns: Date, Service Type, KM, Amount, Payer */}
        {records.length > 0 && (
          <div className="border rounded-lg overflow-hidden" data-testid="records-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="w-8"></th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Service Type</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">KM Reading</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Amount</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-gray-500">Payer</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 w-16">Report</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {records.map((record, idx) => (
                  <React.Fragment key={record.id}>
                    <tr
                      className={`border-b hover:bg-gray-50 cursor-pointer ${record.is_accident_related ? 'bg-red-50/30' : ''}`}
                      onClick={() => setExpandedRecord(expandedRecord === record.id ? null : record.id)}
                      data-testid={`record-row-${idx}`}
                    >
                      <td className="px-2 py-2.5 text-gray-400">
                        {expandedRecord === record.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </td>
                      <td className="px-3 py-2.5 text-gray-900 whitespace-nowrap text-xs font-medium">{formatDate(record.date)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${SERVICE_TYPE_COLORS[record.service_type] || SERVICE_TYPE_COLORS.other}`}>
                            {SERVICE_TYPE_LABELS[record.service_type] || record.service_type}
                          </span>
                          {record.is_accident_related && (
                            <span title="Accident Related" className="text-red-500"><ShieldAlert className="h-3.5 w-3.5" /></span>
                          )}
                          {record.is_flood_damage && (
                            <span title="Flood Damage" className="text-blue-500"><AlertTriangle className="h-3.5 w-3.5" /></span>
                          )}
                          {record.is_major_repair && (
                            <span title="Major Repair" className="text-orange-500"><Wrench className="h-3.5 w-3.5" /></span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right text-gray-700 text-xs whitespace-nowrap">{formatKm(record.km_reading)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-900 text-xs font-medium whitespace-nowrap">{formatAmount(record.invoice_amount)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-medium capitalize ${PAYER_COLORS[record.payer] || PAYER_COLORS.unknown}`}>
                          {record.payer}
                        </span>
                      </td>
                      <td className="px-2 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                        <Switch
                          checked={record.is_public}
                          onCheckedChange={(val) => toggleVisibility(record.id, val)}
                          className="scale-75"
                          data-testid={`toggle-visibility-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-2.5" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => deleteRecord(record.id)}
                          className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                          data-testid={`delete-record-${idx}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                    {/* Expanded Details: Dealer info + detailed services */}
                    {expandedRecord === record.id && (
                      <tr className="bg-slate-50/80 border-b">
                        <td colSpan={8} className="px-6 py-3">
                          <div className="space-y-2.5">
                            {/* Dealer Info */}
                            {record.dealer_name && (
                              <div className="flex items-center gap-2 text-xs">
                                <Store className="h-3.5 w-3.5 text-gray-400" />
                                <span className="font-medium text-gray-700">Dealer:</span>
                                <span className="text-gray-600">{record.dealer_name}</span>
                              </div>
                            )}

                            {/* Detailed Services */}
                            <div className="text-xs">
                              <span className="font-medium text-gray-700">Services Performed:</span>
                              <p className="mt-0.5 text-gray-600 leading-relaxed">{record.services || 'No details available'}</p>
                            </div>

                            {/* Parts Replaced */}
                            {record.parts_replaced && (
                              <div className="text-xs">
                                <span className="font-medium text-gray-700">Parts Replaced:</span>
                                <p className="mt-0.5 text-gray-600">{record.parts_replaced}</p>
                              </div>
                            )}

                            {/* Source File */}
                            <div className="text-[10px] text-gray-400 pt-1 border-t border-gray-100">
                              Source: {record.source_file || '-'}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>)}
    </TabsContent>
  );
}


function DownloadServiceHistoryPdf({ inspectionId }) {
  const [generating, setGenerating] = useState(false);
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  const handleDownload = async () => {
    setGenerating(true);
    try {
      // Open download URL immediately to preserve Safari user gesture context
      const directUrl = `${API}/api/inspections/${inspectionId}/service-history-pdf-direct?token=${encodeURIComponent(token)}`;
      window.open(directUrl, '_blank');
      
      toast.success('Service History PDF download started');
    } catch (err) {
      toast.error(err.message || 'Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={handleDownload} disabled={generating} data-testid="download-sh-pdf-btn">
      {generating ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Generating...</> : <><FileDown className="h-3.5 w-3.5 mr-1" /> Download PDF</>}
    </Button>
  );
}
